import Tag from '@components/Tag';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import { LegStatus } from '@api/Trinium/types';
import { useFetchLegQuery } from '@api/Trinium/Endpoints/Leg';
import DateSlot from '@components/DataGrid/DateSlot';
import LegOrderHeaderSkeleton from './LegOrderHeaderSkeleton';

import gStyles from '@/Global.module.css';
import styles from './StreetTurnHeader.module.css';

interface StreetTurnHeaderProps {
	terminalNumber: string;
	legId?: number;
}

/**
 * Modal sub-header to display Street Turn Information
 */
export default function StreetTurnHeader(props: StreetTurnHeaderProps) {
	const { data: leg, isFetching } = useFetchLegQuery(
		{
			terminalNumber: props.terminalNumber,
			id: Number(props.legId)
		},
		{
			skip: !props.legId
		}
	);

	if (isFetching || !leg) {
		return <LegOrderHeaderSkeleton />;
	}

	return (
		<div className={`${styles.header} ${styles['leg-header']}`}>
			<span className={`${gStyles['label']} ${styles['order-lbl']}`}>Order #</span>
			<div className={styles.order}>
				<span className={gStyles.bold}>{leg.jobId.toString()}</span>
				<span className={gStyles.secondary}>{`/${leg.csn.no.toString().padStart(3, '0')}`}</span>
			</div>
			<span className={`${gStyles['label']} ${styles['job-cat-lbl']}`}>Job Category</span>
			<span className={`${gStyles.bold} ${styles['job-cat']}`}>{leg.job?.cat ? `${leg.job.catTransType} (${leg.job.cat})` : '-'}</span>

			<div className={styles['tag-group']}>
				<Tag
					text='LIVE'
					tagStyle={leg.csn.isLive ? 'live' : 'disabled'}
					height='20px'
					className={styles['tag']}
				/>

				<Tag
					text='HAZ'
					tagStyle={leg.isHazmat ? 'haz' : 'disabled'}
					height='20px'
					className={styles['tag']}
				/>

				<Tag
					text='WGT'
					tagStyle={leg.csn.isOverweight ? 'wgt' : 'disabled'}
					height='20px'
					className={styles['tag']}
				/>

				<Tag
					text='BULK'
					tagStyle={leg.csn.isBulk ? 'bulk' : 'disabled'}
					height='20px'
					className={styles['tag']}
				/>
			</div>

			<span className={`${gStyles['label']} ${styles['leg-label']}`}>Leg</span>

			<span className={`${styles['leg']} ${gStyles.bold}`}>{`${String(leg.no)} - ${String(leg.type)}`}</span>

			<span className={`${gStyles['label']} ${styles['status-lbl']}`}>Status</span>
			<Tag
				text={getEnumValueByKey(LegStatus, leg.status)}
				tagStyle={leg.status}
				className={styles.status}
				height={25}
			/>

			<span className={`${gStyles['label']} ${styles['assigned-lbl']}`}>
				{leg.drv?.type === 'D' ? 'Driver' : leg.drv?.type === 'B' ? 'Broker' : 'Assigned'}
			</span>
			<span
				className={`${styles.assigned} ${gStyles.bold}`}
				title={`${leg.drv?.name ?? ''} ${leg.drv?.type === 'D' ? (leg.drvId ?? '') : ''}`}
			>
				{leg.drv?.type === 'D' ? `${leg.drv.name ?? ''} (${leg.drvId ?? ''})` : leg.drv?.type === 'B' ? leg.drv.name : 'Unassigned'}
			</span>

			<span className={`${gStyles['label']} ${styles['container-lbl']}`}>Container</span>
			<div className={styles.container}>
				<div
					className={styles['container-provider']}
					title={leg.cntrProviderName}
				>
					{leg.cntrProviderName}
				</div>
				<div className={styles['container-info']}>
					{leg.cntrSize} {leg.cntrType}
				</div>
			</div>

			<span className={`${gStyles['label']} ${styles['delivery-lbl']}`}>Delivery</span>
			<div className={styles.delivery}>
				{leg.deliveryLocation && (
					<>
						<div
							className={styles['delivery-name']}
							title={leg.deliveryLocation.name}
						>
							{leg.deliveryLocation.name}
						</div>
						<div className={styles['delivery-address']}>
							{leg.deliveryLocation.city}, {leg.deliveryLocation.state}
						</div>
					</>
				)}
				{(leg.scheduledDeliveryDateFrom || leg.scheduledDeliveryDateTo) && (
					<div className={styles['delivery-date']}>
						<DateSlot
							fromDate={leg.scheduledDeliveryDateFrom}
							toDate={leg.scheduledDeliveryDateTo}
							showTimeInTitle
						/>
					</div>
				)}
			</div>

			<span className={`${gStyles['label']} ${styles['tractor-lbl']}`}>Truck</span>
			<span className={`${styles.tractor} ${gStyles.bold}`}>{leg.tractorId ?? '-'}</span>
		</div>
	);
}
